import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FormProvider, useForm } from "react-hook-form";

import {
  TaskPriority,
  TaskStatus,
} from "@fartherfinance/frontend/api/Tasks/Types";

import Drawer, {
  standardDrawer,
} from "@src/multiCustodian/components/Drawer/Drawer";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import TaskFormV3, {
  TaskFormData,
} from "@src/multiCustodian/components/TaskForm/TaskFormV3";
import TaskProgressChip from "@src/multiCustodian/pages/Advisor/Tasks/TaskProgressIndicator/TaskProgressChip";
import TaskStatusIcon from "@src/multiCustodian/pages/Advisor/Tasks/TaskStatusIcon/TaskStatusIcon";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import Text from "@src/sharedComponents/Typography/Text";

import { ROOT_TASK_IDS, ROOT_TASK_TYPE_MAPPING } from "./constants";
import { createVirtualTaskId, VirtualTask } from "./types";
import {
  mapFormDataToVirtualTask,
  mapVirtualTaskToFormData,
} from "./virtualTaskMappers";

// Re-use the existing stylesheet from TaskSubtasks to preserve the same look & feel
import createTaskStyles from "@src/multiCustodian/pages/Advisor/Tasks/CreateTask/CreateTask.module.css";
import styles from "@src/multiCustodian/pages/Advisor/Tasks/TaskDetails/Components/TaskSubtasks/TaskSubtasks.module.css";

// good enough for temporary, front-end–only data
const generateId = (): string => Math.random().toString(36).substring(2, 10);

interface VirtualTaskDrawerProps {
  task: VirtualTask;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updated: VirtualTask) => void;
  parentTitle?: string;
}

const VirtualTaskDrawer: React.FC<VirtualTaskDrawerProps> = ({
  task,
  isOpen,
  onClose,
  onUpdate,
  parentTitle,
}) => {
  const [editedChildren, setEditedChildren] = useState<VirtualTask[]>(
    task.children
  );

  const isRootTask = ROOT_TASK_IDS.includes(task.id);

  // Get default form data
  const defaultFormData = mapVirtualTaskToFormData(task) as any;

  // If it's a root task, set the appropriate task type
  if (isRootTask && ROOT_TASK_TYPE_MAPPING[task.id]) {
    defaultFormData.taskType = ROOT_TASK_TYPE_MAPPING[task.id];
  }

  const form = useForm<TaskFormData>({
    mode: "onChange",
    defaultValues: defaultFormData,
  });

  const { enableTaskServiceTeams } = useFlags();

  const handleSave = (data: TaskFormData): void => {
    const updated = mapFormDataToVirtualTask(task, data);
    updated.children = editedChildren;
    updated.isNew = false;
    onUpdate(updated);
    onClose();
  };

  const isNew = task.isNew === true;

  return (
    <Drawer
      isDrawerOpen={isOpen}
      onClose={onClose}
      PaperProps={{ style: standardDrawer }}
      footer={
        <div className={createTaskStyles.actions}>
          <Button
            variant="outlined"
            buttonType="primary"
            text="Cancel"
            onClick={onClose}
          />
          <Button
            variant="contained"
            buttonType="primary"
            text="Save"
            onClick={form.handleSubmit(handleSave)}
          />
        </div>
      }
    >
      <FormProvider {...form}>
        <div
          className={createTaskStyles.header}
          style={{ flexDirection: "column", alignItems: "flex-start" }}
        >
          {parentTitle && (
            <Text
              variant="body"
              color="textSubtle"
              style={{ cursor: "pointer", marginBottom: "4px" }}
              onClick={onClose}
            >
              Parent Task: {parentTitle}
            </Text>
          )}
          {isNew && <Text variant="title2">Create Subtask</Text>}
        </div>

        <TaskFormV3
          isServiceTeamFieldVisible={Boolean(enableTaskServiceTeams)}
          isSubtaskCreation={false}
          disableTaskType={isRootTask}
          hideClientField={true}
          hideCustodianField={true}
          hideAdvisorField={true}
          disableTitleField={isRootTask}
        />
        <Spacer verticalSpacing="24px" />

        <VirtualSubtasks
          tasks={editedChildren}
          onTasksChange={setEditedChildren}
          allowAdd={task.isRoot === true}
          parentTitle={task.title}
        />
      </FormProvider>
    </Drawer>
  );
};

interface VirtualSubtasksProps {
  tasks: VirtualTask[];
  onTasksChange: (tasks: VirtualTask[]) => void;
  /**
   * When false the "create subtask" button / icon is hidden – used for the root
   * list that must stay fixed (exactly 6 predefined tasks).
   */
  allowAdd?: boolean;
  parentTitle?: string;
}

const VirtualSubtasks: React.FC<VirtualSubtasksProps> = ({
  tasks,
  onTasksChange,
  allowAdd = true,
  parentTitle,
}) => {
  const [selectedTask, setSelectedTask] = useState<VirtualTask | null>(null);

  const handleCreateSubtask = (): void => {
    const newTask: VirtualTask = {
      id: createVirtualTaskId(generateId()),
      title: "",
      taskStatus: TaskStatus.enum.NOT_STARTED,
      children: [],
      description: "",
      dueDate: null,
      taskPriority: TaskPriority.enum.MEDIUM,
      isNew: true,
    };

    // open drawer without inserting into list yet
    setSelectedTask(newTask);
  };

  const handleOpenTask = (task: VirtualTask): void => {
    setSelectedTask(task);
  };

  if (tasks.length === 0) {
    if (!allowAdd) {
      return null;
    }

    return (
      <Button
        variant="text"
        buttonType="primary"
        style={{ justifyContent: "unset" }}
        text="Create Subtask"
        startIcon={<AddIcon className={styles.createIcon} />}
        onClick={handleCreateSubtask}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Text variant="title3">Subtasks</Text>
          <TaskProgressChip
            variant="counter-and-label"
            subtasks={tasks as unknown as any[]}
          />
        </div>

        {allowAdd && (
          <IconButton
            size="small"
            iconClassName={styles.icon}
            onClick={handleCreateSubtask}
            IconComponent={AddIcon}
          />
        )}
      </div>

      <div>
        {tasks.map((task) => (
          <React.Fragment key={task.id}>
            <div
              className={styles.subtask}
              onClick={() => handleOpenTask(task)}
            >
              <div className={styles.subtaskDetails}>
                <TaskStatusIcon taskStatus={task.taskStatus} />

                {task.taskStatus !== TaskStatus.enum.CANCELED ? (
                  <Text variant="body">{task.title}</Text>
                ) : (
                  <Text variant="body" color="textSubtle">
                    {task.title} - Canceled
                  </Text>
                )}
              </div>

              <IconButton
                size="small"
                iconClassName={styles.icon}
                onClick={() => handleOpenTask(task)}
                IconComponent={ChevronRightIcon}
              />
            </div>

            {/* No inline expand – opens drawer instead */}
          </React.Fragment>
        ))}
      </div>

      {/* Drawer for editing selected task */}
      {selectedTask && (
        <VirtualTaskDrawer
          task={selectedTask}
          isOpen={true}
          parentTitle={parentTitle}
          onClose={() => setSelectedTask(null)}
          onUpdate={(updatedTask) => {
            const exists = tasks.some((t) => t.id === updatedTask.id);
            const updatedTasks = exists
              ? tasks.map((t) => (t.id === updatedTask.id ? updatedTask : t))
              : [...tasks, { ...updatedTask, isNew: false }];

            onTasksChange(updatedTasks);
            setSelectedTask(null);
          }}
        />
      )}
    </div>
  );
};

export default VirtualSubtasks;
